import { SubmitInfo, SubmitInfoCourse, SubmitInfoMinor } from "@app/core/domain/submit-proposal";
import {
  MinorInProposal,
  SubmitProposalStateService,
} from "@app/features/submit-proposal/services/submit-proposal-state.service";
import { CourseData } from "@app/shared/types/course-data";
import { TranslateService } from "@ngx-translate/core";
import { CourseDataMapper } from "@shared/services/course-data-mapping.service";

export interface SubmitCourseItem {
  selected: boolean;
  selectable: boolean;
  disabled: boolean;
  course: SubmitInfoCourse;
  courseData: CourseData;
  onSelect?: () => void;
}

export interface SubmitCourseGroup {
  title: string;
  submitCourseItems: SubmitCourseItem[];
  minorInProposal?: MinorInProposal; // optional, only used for minors
}

export class SubmitChoiceCourseListBuilder {
  constructor(
    private readonly translate: TranslateService,
    private readonly courseDataMapper: CourseDataMapper,
    private readonly submitProposalStateService: SubmitProposalStateService,
  ) {}

  buildElectivesCourseGroups(submitInfo: SubmitInfo): SubmitCourseGroup[] {
    const individualArrangementsGroups = this.buildElectivesIndividualArrangementsGroups(submitInfo);
    const minorsGroups = this.buildMinorGroups(submitInfo);
    const otherElectivesCourseGroups = this.buildOtherElectivesCourseGroups(submitInfo);

    return [...individualArrangementsGroups, ...minorsGroups, ...otherElectivesCourseGroups];
  }

  buildOtherCourseGroups(submitInfo: SubmitInfo): SubmitCourseGroup[] {
    const groupList: SubmitCourseGroup[] = [];
    if (submitInfo?.otherExamComponents?.length) {
      submitInfo.otherExamComponents.forEach((otherExamComponent) => {
        const groupTitle = otherExamComponent.specialisationName
          ? `${otherExamComponent.name} (${otherExamComponent.specialisationName})`
          : otherExamComponent.name;
        const group = this.createReadonlyCourseGroup(groupTitle, otherExamComponent.courses);
        if (group.submitCourseItems.length) {
          groupList.push(group);
        }
      });
    }
    return groupList;
  }

  private buildElectivesIndividualArrangementsGroups(submitInfo: SubmitInfo): SubmitCourseGroup[] {
    const groupList: SubmitCourseGroup[] = [];
    if (submitInfo?.electives.individualArrangements?.length) {
      const group = this.createReadonlyCourseGroup(
        this.translate.instant("submitProposal.coursesIndividualArrangements") as string,
        submitInfo.electives.individualArrangements,
      );
      if (group.submitCourseItems.length) {
        groupList.push(group);
      }
    }
    return groupList;
  }

  private createReadonlyCourseGroup(title: string, courses: SubmitInfoCourse[]): SubmitCourseGroup {
    const group = this.buildCourseGroup(title);
    courses.forEach((course) => {
      this.addCourseToGroup(course, group, { selected: true, selectable: false, disabled: true });
    });
    return group;
  }

  private buildMinorGroups(submitInfo: SubmitInfo): SubmitCourseGroup[] {
    const minorGroups: SubmitCourseGroup[] = [];

    if (submitInfo.electives.minors?.length) {
      submitInfo.electives.minors.forEach((minor) => {
        if (this.submitProposalStateService.planAllowsChangeExamComponent()) {
          // when placing is done in the PlanApp, show individual minor exam components with the minor name between brackets
          // and mark courses as selected, because they are already placed in the PlanApp
          this.createCourseGroupPerMinorComponent(minor, submitInfo, minorGroups);
        } else {
          // placing is done by the SVO
          // show courses per minor, not per minor exam component
          this.createCourseGroupPerMinor(minor, submitInfo, minorGroups);
        }
      });
    }

    return minorGroups;
  }

  /**
   * Show courses grouped per minor, ignore minor exam components
   */
  private createCourseGroupPerMinor(minor: SubmitInfoMinor, submitInfo: SubmitInfo, minorGroups: SubmitCourseGroup[]) {
    const minorInProposal = this.submitProposalStateService.minorsInProposal.find((thisMinorInProposal) =>
      thisMinorInProposal.isEqual(minor),
    )!;
    const minorGroup = this.buildCourseGroup(minor.name);

    this.findAllMinorCourses(minor).forEach((minorCourse) => {
      if (this.isInIndividualArrangement(submitInfo, minorCourse)) {
        // According to tests for TopDesk I 2501 0455 this cannot occur. Maybe it should? https://caci-nl.atlassian.net/browse/OB-31355
      } else {
        this.addCourseToGroup(
          minorCourse,
          minorGroup,
          {
            selected: this.submitProposalStateService.planAllowsChangeExamComponent(),
            selectable: !this.submitProposalStateService.planAllowsChangeExamComponent(),
            disabled: this.submitProposalStateService.planAllowsChangeExamComponent(),
          },
          () => {
            this.submitProposalStateService.selectElectivesCourse(submitInfo, minorCourse, minor);
          },
        );
      }
    });
    if (minorGroup.submitCourseItems.length) {
      minorGroup.minorInProposal = minorInProposal;
      minorGroups.push(minorGroup);
    }
  }

  /**
   * Show minor courses under individual minor exam components with the minor name between brackets,
   * and mark courses as selected.
   */
  private createCourseGroupPerMinorComponent(
    minor: SubmitInfoMinor,
    submitInfo: SubmitInfo,
    minorGroups: SubmitCourseGroup[],
  ) {
    minor.examComponents.forEach((minorComponent) => {
      const minorComponentGroup = this.buildCourseGroup(`${minorComponent.name} (${minor.name})`);

      const minorComponentInProposal = this.submitProposalStateService.minorComponentsInProposal.find(
        (thisMinorComponentInProposal) => thisMinorComponentInProposal.isEqual(minorComponent),
      )!;

      minorComponent.courses.forEach((minorCourse) => {
        if (this.isInIndividualArrangement(submitInfo, minorCourse)) {
          // According to tests for TopDesk I 2501 0455 this cannot occur. Maybe it should? https://caci-nl.atlassian.net/browse/OB-31355
        } else {
          this.addCourseToGroup(minorCourse, minorComponentGroup, {
            selected: this.submitProposalStateService.planAllowsChangeExamComponent(),
            selectable: !this.submitProposalStateService.planAllowsChangeExamComponent(),
            disabled: this.submitProposalStateService.planAllowsChangeExamComponent(),
          });
        }
      });

      if (minorComponentGroup.submitCourseItems.length) {
        minorGroups.push(minorComponentGroup);
      }

      // add all courses to the minorComponentInProposal, because they are already selected when placing via PlanApp
      minorComponentInProposal.selectedCourses.push(...minorComponent.courses);
      // count points of automatically selected courses
      minorComponent.courses.forEach((minorCourse) => {
        this.submitProposalStateService.countPointsOfAutomaticallySelectedCourse(minorCourse);
      });
    });
  }

  private findAllMinorCourses(minor: SubmitInfoMinor): SubmitInfoCourse[] {
    return minor.examComponents.flatMap((minorComponent) => minorComponent.courses);
  }

  private isInIndividualArrangement(submitInfo: SubmitInfo, course: SubmitInfoCourse): boolean {
    return submitInfo.electives.individualArrangements.some((c) => c.id === course.id);
  }

  private buildOtherElectivesCourseGroups(submitInfo: SubmitInfo): SubmitCourseGroup[] {
    const groups: SubmitCourseGroup[] = [];
    const coursesInElectives: SubmitInfoCourse[] = submitInfo.electives.courses;

    if (this.submitProposalStateService.planAllowsChangeExamComponent()) {
      // student has already placed courses in Electives exam component
      // count points of automatically selected courses
      coursesInElectives.forEach((course) => {
        this.submitProposalStateService.countPointsOfAutomaticallySelectedCourse(course);
      });
    }

    if (coursesInElectives.length) {
      const group = this.buildCourseGroup(this.translate.instant("submitProposal.courses") as string);
      coursesInElectives.forEach((course) => {
        // if planAllowsChangeExamComponent, the course is already placed,
        // so in that case add it in the same way as courses in individual arrangements,
        // otherwise add it as selectable course
        this.addCourseToGroup(
          course,
          group,
          {
            selected: this.submitProposalStateService.planAllowsChangeExamComponent(),
            selectable: !this.submitProposalStateService.planAllowsChangeExamComponent(),
            disabled: this.submitProposalStateService.planAllowsChangeExamComponent(),
          },
          () => {
            this.submitProposalStateService.selectElectivesCourse(submitInfo, course);
          },
        );
      });

      if (group.submitCourseItems.length) {
        groups.push(group);
      }
    }

    return groups;
  }

  private buildCourseGroup(header: string, items: SubmitCourseItem[] = []): SubmitCourseGroup {
    return {
      title: header,
      submitCourseItems: items,
    };
  }

  private addCourseToGroup(
    course: SubmitInfoCourse,
    group: SubmitCourseGroup,
    options: { selected: boolean; selectable: boolean; disabled: boolean } = {
      selected: false,
      selectable: true,
      disabled: false,
    },
    onSelect?: () => void,
  ): void {
    const courseData = this.courseDataMapper.mapSubmitInfoCourseData(course);
    group.submitCourseItems.push({
      selected: options.selected,
      selectable: options.selectable,
      disabled: options.disabled,
      course,
      courseData,
      onSelect,
    });
  }
}
